
import { AsyncPipe, PercentPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, output } from '@angular/core';
import { ProjectObserverService } from '@app/cem/project/services/project-observer.service';
import { AssetSuperTypeId, ModuleEnum, PadConstructionProjectSubTypeId, ProjectTypeId, SWDFacilityProjectSubTypeId } from '@app/shared/data/project-enums';
import { EPSILON, round } from '@app/shared/utilities/math-utilities';
import { PanelBarModule } from '@progress/kendo-angular-layout';
import { ProjectBaseScenarioTabItemComponent } from 'app/cem/project/content/project-base-scenario-tabitem.component';
import { AssetMappingConfiguration } from 'app/cem/project/models/asset-mapping-configuration';
import { Observable, of as observableOf } from 'rxjs';
import { ProjectScreenBehaviorService } from '../../services/project-screenbehavior.service';
import { GeneralProjectService } from './../../services/general-project.service';
import { ProjectAssetMappingCostCenterComponent } from './project-asset-mapping-cost-center/project-asset-mapping-cost-center.component';
import { ProjectAssetMappingGeneralAssetComponent } from './project-asset-mapping-general-asset/project-asset-mapping-general-asset.component';
import { ProjectAssetMappingMultiAssetComponent } from './project-asset-mapping-multi-asset/project-asset-mapping-multi-asset.component';
import { ProjectAssetMappingMultiWellComponent } from './project-asset-mapping-multi-well/project-asset-mapping-multi-well.component';
import { ProjectAssetMappingWellDetailsComponent } from './project-asset-mapping-well-details/project-asset-mapping-well-details.component';

@Component({
  selector: 'app-project-asset-mapping',
  templateUrl: './project-asset-mapping.component.html',
  styleUrls: ['./project-asset-mapping.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AsyncPipe,
    PanelBarModule,
    PercentPipe,
    ProjectAssetMappingCostCenterComponent,
    ProjectAssetMappingGeneralAssetComponent,
    ProjectAssetMappingMultiAssetComponent,
    ProjectAssetMappingMultiWellComponent,
    ProjectAssetMappingWellDetailsComponent,
  ],
})
export class ProjectAssetMappingComponent extends ProjectBaseScenarioTabItemComponent implements OnInit {
  public generalProjectService = inject(GeneralProjectService);
  public screenBehaviors = inject(ProjectScreenBehaviorService);
  private poSvc = inject(ProjectObserverService);

  public showAllocationMessage: boolean;
  public allocationMessage = 'Allocation must equal 100%';
  public assetMappingConfigurations: Observable<AssetMappingConfiguration[]>;
  public moduleExpandMap: Map<number, boolean>;
  public projectTotalAllocation = 0;
  public isOwidValueMissing: boolean;
  public requireAssetAllocation: boolean;
  public module = ModuleEnum;

  readonly tabTitle = output<string>();
  readonly tabTdv = output<boolean>();
  readonly owidMissing = output<boolean>();

  constructor() {
    super();
  }

  public async ngOnInit() {
    this.loadModule();
    this.isOwidValueMissing = false;
    const behaviors = await this.screenBehaviors.getByProjectTypeId(this.projectTab().ProjectTypeId);

    var behavior = this.screenBehaviors.getBehaviorRule(behaviors, 'app-project-asset-mapping', 'AssetMapping');
    this.requireAssetAllocation = !behavior || behavior.Visible; //if no behavior assume true
  }

  private loadModule() {
    this.loadModuleConfiguration();
    this.setAllocation();
    this.setModuleAllocationProperties();
    this.setTDV();
  }

  private loadModuleConfiguration(): Promise<any> {
    var promise = this.generalProjectService.getAssetMappingConfiguration(this.projectTab().ProjectTypeId);
    promise.then((x) => {
      const projectTab = this.projectTab();
      if (projectTab.ProjectTypeId === ProjectTypeId.PadConstruction) {
        x = this.HandlePadConstructionAllocation(x);
      }
      if (projectTab.ProjectTypeId === ProjectTypeId.SaltWaterDisposal) {
        x = this.HandleSwdFacilityAllocation(x);
      }
      let data = x.sort((n1, n2) => n1.Sequence - n2.Sequence);
      let assetMappingConfig = data.map(config => new AssetMappingConfiguration(config));
      this.assetMappingConfigurations = observableOf(assetMappingConfig);
      this.moduleExpandMap = new Map<number, boolean>();
      assetMappingConfig.map(t => { this.moduleExpandMap.set(t.AssetMappingConfigurationId, t.ModuleProperties.Show); });
      this.setModuleEditableProperty();
    });
    return promise;
  }

  private HandlePadConstructionAllocation(configs: AssetMappingConfiguration[]) {
    // Filter asset mapping configuration
    if (this.projectTab().ProjectSubTypeId === PadConstructionProjectSubTypeId.TankBattery) {
      // Exclude PPID asset mapping configuration
      configs = configs.filter(config => config.AssetSuperTypeId !== AssetSuperTypeId.PPID);
    } else {
      // Exclude tank battery asset mapping configuration
      configs = configs.filter(config => config.AssetSuperTypeId !== AssetSuperTypeId.Facility);
    }

    return configs;
  }

  private HandleSwdFacilityAllocation(configs: AssetMappingConfiguration[]) {
    // Filter asset mapping configuration
    if (this.projectTab().ProjectSubTypeId !== SWDFacilityProjectSubTypeId.ProducedWaterPipeline) {
      // Exclude tank battery asset mapping configuration
      configs = configs.filter(config => config.IsTankBatterySource !== true);
    }

    return configs;
  }

  private setModuleEditableProperty() {
    let projectStatusId = this.projectTab().ProjectStatusId;
    this.assetMappingConfigurations.subscribe(config => {
      config.map(x => {
        let editIndex = x.ProjectStatusEditable.findIndex(t => t.ProjectStatusId === projectStatusId);
        x.ModuleProperties.IsMappingEditable = editIndex === -1 ? false : true;
      });
    });
  }

  private setAllocation() {
    this.projectTab().ProjectAssetMappingService.getProjectTotalAllocation()
      .subscribe(x => {
        if (x) {
          let pct = round(x, 6);
          this.projectTotalAllocation = pct;
          this.poSvc.projectObserve('assetmapping', pct === 1);

          if (pct !== 1 && this.requireAssetAllocation)
            this.showAllocationMessage = true;
          else
            this.showAllocationMessage = false;

        } else {
          this.projectTotalAllocation = 0;
          this.showAllocationMessage = this.requireAssetAllocation;
        }
      });
  }

  private setModuleAllocationProperties() {
    this.projectTab().ProjectAssetMappingService.getProjectModuleAllocationProperties()
      .subscribe((data) => {
        data.map((module) => {
          if (module.isToggle)
            this.moduleExpandMap.set(module.assetMappingConfigurationId, module.isActiveToggle);
        });
      });
  }

  private setTDV(): void {
    this.projectTab().ProjectAssetMappingService.hasPendingChanges.subscribe((hasChange) => {
      let title = hasChange ? 'ASSET ALLOCATION*' : 'ASSET ALLOCATION';
      this.tabTdv.emit(hasChange);
      this.tabTitle.emit(title);
    });
    this.projectTab().ProjectAssetMappingService.getProjectTotalAllocation().subscribe((x) => {
      if (Math.abs(x - 1) > EPSILON) {
        this.tabTdv.emit(false);
      }
    })
  }

  private clearTDV(): void {
    let newTitle: string = 'ASSET Allocation';
    this.tabTitle.emit(newTitle);
    let tdvState: boolean = false;
    this.tabTdv.emit(tdvState);
  }

  public readOwidFlag(e: any) {
    this.isOwidValueMissing = e;
    let x: any = e;
    this.owidMissing.emit(this.isOwidValueMissing);
  }

  public saveChanges() {
    this.projectTab().ProjectAssetMappingService.saveChanges();
  }

  public cancelChanges() {
    this.projectTab().ProjectAssetMappingService.cancelChanges();
    this.clearTDV();
  }

  public reactivateAssetTab(): Promise<any> {
    return this.loadModuleConfiguration();
  }
}